import { Clock } from 'three';
import { Assets } from '@/src/assets';
import { Debug } from '@/src/setup/utils/common';
import { GameBoy } from '@/src/game-boy/GameBoy';
import { Performance } from '@/src/setup/utils/Performance';
import { Resizer } from '@/src/setup/utils/resizer';
import { Octree } from 'three/examples/jsm/math/Octree.js';
import { Timestamp } from '@/src/setup/utils/Timestamp';
import { Renderer, Camera, Scene } from '@/src/setup';
import { MarioCartridge } from '@/src/game-boy/cartridges/MarioCartridge';
import { DoomCartridge } from '@/src/game-boy/cartridges/DoomCartridge';
import { QuakeCartridge } from '@/src/game-boy/cartridges/QuakeCartridge';
import { DiggerCartridge } from '@/src/game-boy/cartridges/DiggerCartridge';
import { DukeCartridge } from '@/src/game-boy/cartridges/DukeCartridge';
import { PlayerController } from '@/src/first-person/controllers/PlayerController';
import { inventory, pause, resume, setRef, setState, show, showModal, showToast, state } from '@/src/setup/store';
import { WolfensteinCartridge } from '@/src/game-boy/cartridges/WolfensteinCartridge';

const isMobileDevice = () => {
  const hasTouch = window.matchMedia('(pointer:coarse)').matches || 'ontouchstart' in window;
  const isNarrowViewport = window.innerWidth < 1024;
  return hasTouch || isNarrowViewport;
};

export function setup() {
  const FPS = 60;
  const DELAY = 1000 / FPS; // millis
  const world = new Octree();
  const clock = new Clock();
  const camera = new Camera();
  const renderer = new Renderer();
  const scene = new Scene(Assets.Room);
  const player = new PlayerController(camera, world);
  const gameBoy = new GameBoy();
  const performance = new Performance();
  const timestamp = new Timestamp();
  const resizer = new Resizer(renderer, camera);

  world.fromGraphNode(Assets.Room.scene);

  gameBoy.scene.position.set(1.6, 1, 1);
  gameBoy.scene.rotation.set(0, -Math.PI / 2, 0);
  gameBoy.scene.scale.multiplyScalar(0.35);

  const marioCartridge = new MarioCartridge(Assets.Cartridge, Assets.Mario);
  const doomCartridge = new DoomCartridge(Assets.Cartridge, Assets.Doom);
  const quakeCartridge = new QuakeCartridge(Assets.Cartridge, Assets.Quake);
  const diggerCartridge = new DiggerCartridge(Assets.Cartridge, Assets.Digger);
  const dukeCartridge = new DukeCartridge(Assets.Cartridge, Assets.Duke);
  const wolfensteinCartridge = new WolfensteinCartridge(Assets.Cartridge, Assets.Wolfenstein);

  const cartridges = [marioCartridge, doomCartridge, quakeCartridge, diggerCartridge, dukeCartridge, wolfensteinCartridge];

  quakeCartridge.scene.position.set(3.477, 0.763, -1.134);
  diggerCartridge.scene.position.set(-0.916, 1.603, 3.562);
  marioCartridge.scene.position.set(-0.636, 0.5, -1.476);
  doomCartridge.scene.position.set(-0.916, 1.603, -0.7);
  dukeCartridge.scene.position.set(0.763, 1.323, 5.801);
  wolfensteinCartridge.scene.position.set(1.763, 1.323, 5.801);

  scene.room.add(...cartridges.map((cartridge) => cartridge.scene));

  scene.add(gameBoy.scene);

  {
    (function onSetup() {
      Debug.enabled() && performance.show();
      resizer.subscribe();
      player.subscribe();
      // Wire up the gameboy to the projector screen
      gameBoy.connectExternalDisplay(scene.projectorScreen);
      setState({ started: true });
      setRef({ player });
      document.addEventListener('pointerlockchange', () => {
        const isLocked = document.pointerLockElement === document.body;
        if (isLocked) resume();
        else {
          if (show.inventory) return;
          if (show.modal) return;
          pause();
        }
      });
    })();
    (function gameLoop() {
      Debug.enabled() && performance.start();
      if (timestamp.delta >= DELAY) {
        // INFO: getDelta() must only be called when a tick is actually consumed,
        // otherwise higher refresh-rate displays (eg. 120Hz) silently halve the
        // simulated delta since a discarded rAF tick would already reset the clock.
        const delta = clock.getDelta();
        const time = clock.getElapsedTime();
        if (!state.isPaused && state.started) {
          player.update(delta);

          if (!gameBoy.equipped) {
            gameBoy.update(time);
            if (player.intersects(gameBoy)) {
              player.pickUp(gameBoy);
              gameBoy.scene.removeFromParent();
              scene['2ndScene'].add(gameBoy.scene);
              gameBoy.scene.visible = true;
              showModal('Info', 'You picked up the Game Boy device. Find cartridges to play.');
            }
          }
          for (const cartridge of cartridges) {
            if (cartridge.equipped) continue;

            if (player.intersects(cartridge)) {
              player.pickUp(cartridge);
              if (inventory.cartridges.length === 1) {
                // First cartridge, show info
                showModal(
                  'Info',
                  isMobileDevice()
                    ? `You picked up <b>${cartridge.name}</b> cartridge. ` +
                        `Tap the Inventory button to open your collection. Select this cartridge and tap Play to insert it into the console. <br> <br>` +
                        'Double-tap on the screen to switch between First Person and Emulator modes. ' +
                        'When in Emulator mode, you can use the ENTER and ESC buttons to interact with the DOS game. FP movement is disabled. <br><br>' +
                        'Use the SHOOT button to change the GameBoy camera position while in First Person mode.'
                    : `You picked up <b>${cartridge.name}</b> cartridge. ` +
                        `Press <kbd>TAB</kbd> to check the inventory. You can select the cartridge from the inventory and play it. <br> <br>` +
                        'You can use the <kbd>M</kbd> key to switch between First Person and Emulator modes. ' +
                        'When Emulator mode is enabled, you are interacting with either GameBoy or Projector screen, everything else is disabled. <br><br>' +
                        'Additionally, you can change GameBoy camera position by pressing <kbd>C</kbd> key.',
                );
              } else {
                showToast(cartridge.name, 'Added to inventory.');
              }
            }

            cartridge.update(time);
          }
        }

        timestamp.update();

        renderer.clear();
        renderer.render(scene, camera);
        renderer.clearDepth();

        if (gameBoy.equipped) {
          gameBoy.adjustBy(camera);
          renderer.render(scene['2ndScene'], camera);
        }
      }

      Debug.enabled() && performance.end();
      requestAnimationFrame(gameLoop);
    })();
  }
}
